import { useEffect, useRef, useState } from 'react';
import { X, Upload, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { usePanelStore } from '../../stores/panelStore';
import { useStudioStore } from '../../stores/studioStore';
import { submitBugReport, type BugSeverity } from '../../lib/bugReporter';

interface Props {
  onClose: () => void;
}

const SEVERITIES: Array<{ value: BugSeverity; label: string }> = [
  { value: 'blocked', label: 'Blocked' },
  { value: 'annoying', label: 'Annoying' },
  { value: 'weird', label: 'Weird' },
  { value: 'suggestion', label: 'Suggestion' },
];

const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;

export function BugReportModal({ onClose }: Props) {
  const { activeSection } = usePanelStore();
  const { activeProject } = useStudioStore();

  const [whatHappened, setWhatHappened] = useState('');
  const [whatTrying, setWhatTrying] = useState('');
  const [severity, setSeverity] = useState<BugSeverity>('annoying');
  const [blocksWork, setBlocksWork] = useState(false);
  const [impact, setImpact] = useState('');
  const [screenshot, setScreenshot] = useState<Blob | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (!e.clipboardData) return;
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) acceptFile(file);
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  const acceptFile = (file: File | Blob) => {
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setSubmitError('Screenshot exceeds the 5MB limit.');
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setScreenshot(file);
    setPreview(URL.createObjectURL(file));
    setSubmitError(null);
  };

  const clearScreenshot = () => {
    if (preview) URL.revokeObjectURL(preview);
    setScreenshot(null);
    setPreview(null);
  };

  const canSubmit =
    whatHappened.trim().length > 0 &&
    whatTrying.trim().length > 0 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);

    const currentView = activeProject
      ? `editor:${activeProject.id}`
      : activeSection ?? null;

    const result = await submitBugReport({
      what_happened: whatHappened.trim(),
      what_trying_to_do: whatTrying.trim(),
      severity,
      blocks_work: blocksWork,
      impact_description: blocksWork ? impact.trim() : '',
      screenshot,
      current_view: currentView,
      extra_context: {
        active_section: activeSection ?? null,
        active_project_id: activeProject?.id ?? null,
        active_project_name: activeProject?.name ?? null,
      },
    });

    if (result.ok) {
      setDone(true);
      setTimeout(onClose, 1500);
    } else {
      setSubmitError(result.error ?? 'Failed to submit bug report.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div>
            <h2 className="text-base font-semibold text-zinc-100">Report a bug</h2>
            <p className="text-xs text-zinc-500 mt-0.5">Goes to the OTTO team.</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="px-5 py-10 flex flex-col items-center gap-3 text-center">
            <CheckCircle2 className="text-emerald-400" size={36} />
            <div className="text-zinc-100 font-medium">Bug report sent</div>
            <div className="text-xs text-zinc-500">Closing…</div>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-4">
            <Field label="What happened?" required>
              <textarea
                value={whatHappened}
                onChange={(e) => setWhatHappened(e.target.value)}
                rows={3}
                placeholder="Describe the problem you saw."
                className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-cyan-500/60 resize-y"
              />
            </Field>

            <Field label="What were you trying to do?" required>
              <textarea
                value={whatTrying}
                onChange={(e) => setWhatTrying(e.target.value)}
                rows={2}
                placeholder="Describe what you were attempting when it happened."
                className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-cyan-500/60 resize-y"
              />
            </Field>

            <Field label="Severity">
              <div className="flex flex-wrap gap-2">
                {SEVERITIES.map((s) => {
                  const active = severity === s.value;
                  return (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setSeverity(s.value)}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-medium uppercase tracking-wider border transition-colors ${
                        active
                          ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40'
                          : 'bg-zinc-950 text-zinc-500 border-zinc-800 hover:text-zinc-300 hover:border-zinc-700'
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </Field>

            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={blocksWork}
                onChange={(e) => setBlocksWork(e.target.checked)}
                className="w-4 h-4 accent-cyan-500 bg-zinc-950 border-zinc-700 rounded"
              />
              This blocks my current work
            </label>

            {blocksWork && (
              <Field label="What can't you do right now?">
                <textarea
                  value={impact}
                  onChange={(e) => setImpact(e.target.value)}
                  rows={2}
                  placeholder="What's stuck because of this?"
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-cyan-500/60 resize-y"
                />
              </Field>
            )}

            <Field label="Screenshot" hint="Optional — attach or paste (Ctrl/Cmd+V)">
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-950 border border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-zinc-600 text-xs transition-colors"
                  >
                    <Upload size={12} /> Attach image
                  </button>
                  {screenshot && (
                    <button
                      type="button"
                      onClick={clearScreenshot}
                      className="px-3 py-1.5 rounded-lg text-zinc-500 hover:text-red-400 text-xs transition-colors"
                    >
                      Remove
                    </button>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) acceptFile(f);
                      e.target.value = '';
                    }}
                  />
                </div>
                {preview && (
                  <div className="relative border border-zinc-800 rounded-lg overflow-hidden bg-black">
                    <img
                      src={preview}
                      alt="Screenshot preview"
                      className="w-full max-h-48 object-contain"
                    />
                  </div>
                )}
              </div>
            </Field>

            {submitError && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div>{submitError}</div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800">
              <button
                onClick={onClose}
                className="px-3 py-1.5 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 text-xs transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white text-xs font-medium transition-colors"
              >
                {submitting ? <Loader2 size={12} className="animate-spin" /> : null}
                Send report
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <label className="text-[11px] font-medium uppercase tracking-wider text-zinc-400">
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
        {hint && <span className="text-[10px] text-zinc-600">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
